/** Portfolio Maps lens keys — keep in sync with backend
 * utils/portfolio_map_lenses_config.py and PortfolioMapTab buttons. */

export const PORTFOLIO_MAP_LENS_KEYS = [
  "bh_fit",
  "net_signal_fit",
  "cluster",
  "cluster_al",
  "ahbh3x3",
  "bh_ah",
] as const;

export type PortfolioMapLensKey = (typeof PORTFOLIO_MAP_LENS_KEYS)[number];

export type PortfolioMapLensVisibility = Record<PortfolioMapLensKey, boolean>;

export type PortfolioMapsConfig = {
  enabled: boolean;
  lenses: PortfolioMapLensVisibility;
};

export const PORTFOLIO_MAP_LENS_OPTIONS: {
  key: PortfolioMapLensKey;
  label: string;
  category: "Business Health" | "Account Health" | "Both";
}[] = [
  { key: "bh_fit", label: "BH × Fit", category: "Business Health" },
  {
    key: "net_signal_fit",
    label: "Net Investment × Fit",
    category: "Business Health",
  },
  {
    key: "cluster",
    label: "AH Risk × Opportunity",
    category: "Account Health",
  },
  { key: "cluster_al", label: "AH × AL", category: "Account Health" },
  { key: "ahbh3x3", label: "AH-BH Matrix", category: "Both" },
  { key: "bh_ah", label: "BH × AH", category: "Both" },
];

export function defaultPortfolioMapsConfig(): PortfolioMapsConfig {
  return {
    enabled: false,
    lenses: Object.fromEntries(
      PORTFOLIO_MAP_LENS_KEYS.map(key => [key, true])
    ) as PortfolioMapLensVisibility,
  };
}

function asBool(value: unknown, defaultValue = true): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(lowered)) return true;
    if (["false", "0", "no", "off"].includes(lowered)) return false;
  }
  if (typeof value === "number") return value === 1;
  return defaultValue;
}

/** Normalize global Portfolio Maps config — master switch defaults off. */
export function normalizePortfolioMapsConfig(
  raw: unknown
): PortfolioMapsConfig {
  const defaults = defaultPortfolioMapsConfig();
  if (!raw || typeof raw !== "object") return defaults;
  const block = raw as Record<string, unknown>;
  const lensesRaw =
    block.lenses && typeof block.lenses === "object"
      ? (block.lenses as Record<string, unknown>)
      : {};
  const lenses = Object.fromEntries(
    PORTFOLIO_MAP_LENS_KEYS.map(key => [
      key,
      key in lensesRaw ? asBool(lensesRaw[key], true) : true,
    ])
  ) as PortfolioMapLensVisibility;
  return {
    enabled: asBool(block.enabled, false),
    lenses,
  };
}

export function isPortfolioMapLensVisible(
  config: PortfolioMapsConfig,
  lensKey: string
): boolean {
  if (!config.enabled) return false;
  if (!(PORTFOLIO_MAP_LENS_KEYS as readonly string[]).includes(lensKey)) {
    return true;
  }
  return config.lenses[lensKey as PortfolioMapLensKey] !== false;
}
